import { useTranslation } from "next-i18next";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Grid } from "@mui/material";
import isEqual from "lodash/isEqual";
import { useRouter } from "next/router";
import { initialStateAvailableFilters } from "@Components/search/utils/algoliaData";
import {
  reduceTooltipData,
  formatStickyFilters,
  algoliaToCTTransformer,
  getSearchHitsByCategory,
} from "@Components/search/utils/searchUtils";
import Icon from "@Components/ui/icon";
import ProductTile from "@Components/ui/productTile/productTile";
import { isCanadianLocale } from "@Utils/locale";
import { useCustomer } from "@wff/hooks/useCustomer";
import { useFavorites } from "@wff/hooks/useFavorites";
import { usePreviousWF } from "@wff/hooks/useMemoize";
import { useAppSelector, useAppDispatch } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import {
  setSearchHits,
  setAvailableFilters,
  setSearchProductHits,
} from "@wff/store/searchSlice";
import {
  DEFAULT_PAGE,
  PRODUCTS_PER_BATCH,
  PRODUCTS_LISTING_FILTER_INITIAL_STATE,
} from "./constants";
import { useProductsFilter } from "./context/ProductsFilterContext";
import { useProductsSearchCriteriaByURL } from "./hooks";
import { StickyToolbar } from "./stickyFilters/StickyToolbar";
// import { Toolbar } from "./toolbar/Toolbar";  commented for future implementation
import { IProductsSearchCriteria, IProductsListingPageContent } from "./types";
import {
  getCountOfSelectedFilterValues,
  changeProductsSearchCriteriaInURL,
} from "./utils";

import classes from "./ProductsListing.module.scss";

export const ProductsListingLayout = (props: IProductsListingPageContent) => {
  const router = useRouter();
  const {
    query: { slug: category },
  } = router;
  const appDispatch = useAppDispatch();
  const searchCriteria = useProductsSearchCriteriaByURL();
  const prevCategory = usePreviousWF(category);
  const prevSearchCriteria = usePreviousWF(searchCriteria);
  const { t } = useTranslation("common");
  const { selectedSortBy, selectedFilterValues, resetFilter, setFilter } =
    useProductsFilter();
  const { productHits } = useAppSelector((state) => state.search);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { languageCode } = useAppSelector(localeSelector);
  const CTProducts = useMemo(
    () =>
      props?.products?.items.filter((product) => !!product?.ctproductSku) || [],
    [props]
  );
  const searchProductsByAlgolia = !CTProducts.length;
  const isBrowser: boolean = ((): boolean => typeof window !== "undefined")();
  const currentLocale = useAppSelector(localeSelector)?.languageCode;
  const isCanadaUser = isCanadianLocale(currentLocale);

  useEffect(() => {
    if (prevCategory !== category) {
      if (searchProductsByAlgolia) {
        const saveSearchResults = (results: any) => {
          const filters = formatStickyFilters(results, category);
          if (!Object.keys(searchCriteria).length) {
            appDispatch(setSearchHits(results?.hits));
            appDispatch(setSearchProductHits(results?.hits));
          }

          appDispatch(setAvailableFilters(filters));
          setIsLoading(false);
        };

        if (
          !isEqual(
            PRODUCTS_LISTING_FILTER_INITIAL_STATE.selectedFilterValues,
            selectedFilterValues
          ) ||
          !isEqual(
            PRODUCTS_LISTING_FILTER_INITIAL_STATE.selectedSortBy,
            selectedSortBy
          )
        ) {
          resetFilter();
          changeProductsSearchCriteriaInURL(router, null);
        }

        setIsLoading(true);
        getSearchHitsByCategory(saveSearchResults, category);
      } else {
        appDispatch(setAvailableFilters(initialStateAvailableFilters));
      }
    }
  }, [
    category,
    searchCriteria,
    prevCategory,
    selectedSortBy,
    selectedFilterValues,
    searchProductsByAlgolia,
    router,
    resetFilter,
    appDispatch,
  ]);

  useEffect(() => {
    const { filterBy, sortBy } = searchCriteria;
    const { filterBy: prevFilterBy, sortBy: prevSortBy } =
      (prevSearchCriteria ?? {}) as IProductsSearchCriteria;

    if (
      searchProductsByAlgolia &&
      (!isEqual(prevFilterBy, filterBy) || !isEqual(prevSortBy, sortBy))
    ) {
      const saveSearchResults = (results: any) => {
        const filters = formatStickyFilters(results, category);
        appDispatch(setSearchHits(results?.hits));
        appDispatch(setSearchProductHits(results?.hits));
        appDispatch(setAvailableFilters(filters));
        setIsLoading(false);
      };

      if (
        !isEqual(selectedFilterValues, filterBy) ||
        !isEqual(selectedSortBy, sortBy)
      ) {
        const countSelectedFilterValues =
          getCountOfSelectedFilterValues(filterBy);

        setFilter({
          selectedSortBy: sortBy,
          selectedFilterValues: filterBy,
          countSelectedFilterValues,
        });
      }

      setIsLoading(true);
      getSearchHitsByCategory(saveSearchResults, category, searchCriteria);
    }
  }, [
    category,
    searchCriteria,
    prevSearchCriteria,
    selectedSortBy,
    selectedFilterValues,
    searchProductsByAlgolia,
    setFilter,
    appDispatch,
  ]);

  const tooltipData = reduceTooltipData(
    props?.["products.tooltip.title"],
    props?.["products.tooltip.description"],
    props?.["products.tooltip.example"]
  );
  const productsShowLimit =
    PRODUCTS_PER_BATCH * (searchCriteria?.page ?? DEFAULT_PAGE);
  const isShowLoadMoreBtn = searchProductsByAlgolia
    ? productHits?.length > productsShowLimit
    : CTProducts?.length > productsShowLimit;
  const productList = (
    searchProductsByAlgolia
      ? algoliaToCTTransformer(productHits, languageCode, tooltipData)
      : CTProducts
  ).slice(0, productsShowLimit);

  const loadMoreHandler = () => {
    const { page = DEFAULT_PAGE } = searchCriteria;

    changeProductsSearchCriteriaInURL(router, {
      ...searchCriteria,
      page: page + 1,
    });
  };

  if (isCanadaUser && isBrowser && isLoading) {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }

  const { favoritesList, getFavoritesList } = useFavorites();
  const { isLoggedIn } = useCustomer();

  const getFavoritesListMemo = useCallback(
    async (type) => {
      await getFavoritesList(type);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    isLoggedIn && getFavoritesListMemo("default");
  }, [getFavoritesListMemo, isLoggedIn]);

  return (
    <div className={classes["products-list-sticky"]}>
      <StickyToolbar {...props} />
      {isLoading ? (
        <div className={classes["loader"]}>
          <Icon iconName="loader" />
        </div>
      ) : (
        <Grid className={classes["results-sticky-wrapper"]}>
          {productList?.length ? (
            <>
              <div className={classes["results-grid"]}>
                {productList?.map((item: any, index: number) => (
                  <ProductTile
                    item={item}
                    key={index}
                    isFavorite={true}
                    favoritesList={favoritesList}
                    isFavoritePage={false}
                    isPLP
                  />
                ))}
              </div>
              <div className={classes["results-loadMore"]}>
                {isShowLoadMoreBtn && (
                  <button
                    data-testid="product-load-more"
                    onClick={loadMoreHandler}
                  >
                    {props["plp.page.btn.LoadMore"]}{" "}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className={classes["results-loadMore"]}>
              <div className={classes["results-loadMore-filter"]}>
                <h2>{t("products.no_products.header")}</h2>
                <h3>{t("products.no_products.message")}</h3>
              </div>
            </div>
          )}
        </Grid>
      )}
    </div>
  );
};
